/**
 * operatorName.js — single source for rendering a human-readable
 * operator label across the app.
 *
 * Display fallback order:
 *   1. Full name             (record.removed_by_name || record.name)
 *   2. Display name alias    (record.display_name)
 *   3. Email local-part      (record.removed_by_email || record.email)
 *   4. "Op xxxxxx"           (last-resort fallback derived from the id)
 *
 * Never returns "Unknown operator" if an id is present, per UI rules.
 */

function emailPrefix(email) {
  if (typeof email !== "string") return "";
  const at = email.indexOf("@");
  return at > 0 ? email.slice(0, at) : email;
}

function idTail(id) {
  if (!id) return "";
  return `Op ${String(id).slice(0, 6)}`;
}

/**
 * Resolve a friendly operator label.
 *
 * @param {object|null} record  Object that may carry name/email fields
 *                              (e.g. an enriched removal row with
 *                              removed_by_name + removed_by_email,
 *                              or a user lookup object with name + email).
 * @param {string|null} id      The operator id (used only for the tail
 *                              fallback when no name/email is available).
 */
export function operatorName(record, id) {
  if (record && typeof record === "object") {
    const name = (
      record.removed_by_name ||
      record.name ||
      record.full_name ||
      record.display_name ||
      ""
    ).trim();
    if (name) return name;

    const email = record.removed_by_email || record.email;
    const prefix = emailPrefix(email);
    if (prefix) return prefix;
  }
  if (id) return idTail(id);
  return "—";
}

/**
 * Build a one-call resolver scoped to a list of removal rows.
 * Returns a function (rowOrId) → label that handles both shapes.
 *
 *   const label = makeOperatorResolver(removals);
 *   label(removalRow)           // → "Jordan Lee"
 *   label("u_abc1234")          // → "Jordan Lee" (looked up by id)
 */
export function makeOperatorResolver(rows = []) {
  const byId = new Map();
  for (const r of rows) {
    if (!r) continue;
    const id = r.removed_by || r.id;
    if (!id) continue;
    // Keep first seen — they should all carry the same enrichment.
    if (!byId.has(id)) byId.set(id, r);
  }
  return function resolve(rowOrId) {
    if (typeof rowOrId === "string") {
      return operatorName(byId.get(rowOrId), rowOrId);
    }
    if (rowOrId && typeof rowOrId === "object") {
      return operatorName(rowOrId, rowOrId.removed_by || rowOrId.id);
    }
    return "—";
  };
}

/**
 * Email-prefix-first helper for surfaces where only an email is known
 * (e.g. HistoryPage action rows that carry `actor_email`).
 */
export function emailToFriendlyName(email) {
  const prefix = emailPrefix(email);
  return prefix || email || "—";
}
